import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Briefcase, Search, MapPin, MoreVertical, ArrowRight, ImageIcon, Zap, ShieldAlert, Clock, CalendarDays,
  Receipt, Truck, Award, ChevronRight, Map as MapIcon, CalendarClock, HardHat, Users,
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AuthImage } from "@/components/AuthImage";
import { initials, pct, nzToday } from "./useDashboardV5";

const TABS = [["in_progress", "In Progress"], ["planned", "Upcoming"], ["completed", "Completed"], ["on_hold", "On Hold"]];
const STATUS_LABEL = { in_progress: "Active", planned: "Planned", on_hold: "On Hold", completed: "Completed" };
const PAGE = 6;
const Skel = () => <span className="v5-skel" aria-hidden="true" />;

function JobRow({ j, usersById }) {
  const nav = useNavigate();
  const crew = (j.assigned_user_ids || []).map((id) => usersById[id]).filter(Boolean);
  const progress = j.task_total > 0 ? pct(j.task_done, j.task_total) : null;
  const open = () => nav(`/app/jobs/${j.id}`);
  return (
    <div className="v5-jobrow" role="row" tabIndex={0} onClick={open} onKeyDown={(e) => { if (e.key === "Enter") open(); }} data-testid={`v5-job-row-${j.id}`}>
      <div className="v5-jobidcell" role="cell">
        {j.thumb_photo_path ? <AuthImage path={j.thumb_photo_path} alt="" className="v5-jobthumb" /> : <span className="v5-jobthumb" aria-hidden="true"><ImageIcon size={16} /></span>}
        <span className="v5-jobno">{j.job_number || "—"}</span>
      </div>
      <div className="v5-jobname" role="cell" title={j.title}>{j.title}</div>
      <div className="v5-joblocation" role="cell" title={j.site_address || ""}><MapPin size={13} /><span>{j.site_address || "No site address"}</span></div>
      <div className="v5-crew" role="cell" aria-label={`${crew.length} crew assigned`}>
        {crew.slice(0, 3).map((u) => <span key={u.id} title={u.name}>{initials(u.name)}</span>)}
        {crew.length > 3 && <span className="plus">+{crew.length - 3}</span>}
        {!crew.length && <small style={{ color: "#9aa4b2" }}>—</small>}
      </div>
      <div className="v5-progress" role="cell" title={progress == null ? "No tasks tracked on this job" : `${j.task_done} of ${j.task_total} tasks done`}>
        {progress == null ? <small>Not tracked</small> : <><b>{progress}%</b><i><em style={{ width: `${progress}%` }} /></i></>}
      </div>
      <div role="cell"><span className={`v5-status ${j.status}`}>{STATUS_LABEL[j.status] || j.status}</span></div>
      <div role="cell" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild><button type="button" className="v5-rowmenu" aria-label={`Actions for ${j.title}`} data-testid={`v5-job-menu-${j.id}`}><MoreVertical size={16} /></button></DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => nav(`/app/jobs/${j.id}`)}>Open job</DropdownMenuItem>
            <DropdownMenuItem onClick={() => nav(`/app/jobs?edit=${j.id}`)}>Edit job</DropdownMenuItem>
            <DropdownMenuItem onClick={() => nav(`/app/timesheets?job_id=${j.id}`)}>Add timesheet for this job</DropdownMenuItem>
            <DropdownMenuItem onClick={() => nav(`/app/safety?job_id=${j.id}`)}>Safety records for this job</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

export function JobsPanel({ jobs, usersById, loading }) {
  const [tab, setTab] = useState("in_progress");
  const [q, setQ] = useState("");
  const [limit, setLimit] = useState(PAGE);
  const counts = useMemo(() => TABS.reduce((m, [k]) => ({ ...m, [k]: (jobs || []).filter((j) => j.status === k).length }), {}), [jobs]);
  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (jobs || []).filter((j) => j.status === tab)
      .filter((j) => !s || [j.title, j.job_number, j.site_address, j.client_name].some((v) => (v || "").toLowerCase().includes(s)));
  }, [jobs, tab, q]);
  return (
    <section className="v5-panel v5-jobsPanel" aria-labelledby="v5-jobs-title" data-testid="v5-jobs-panel">
      <div className="v5-panelHead">
        <h2 id="v5-jobs-title"><span className="v5-sectionIcon"><Briefcase /></span> Jobs <small className="v5-accent">● {loading ? "…" : counts.in_progress} ACTIVE</small></h2>
        <Link className="v5-textBtn" to="/app/jobs" data-testid="v5-jobs-view-all">View all <ArrowRight size={14} /></Link>
      </div>
      <div className="v5-jobTools">
        <div className="v5-tabs" role="tablist" aria-label="Job status">
          {TABS.map(([k, label]) => (
            <button key={k} role="tab" aria-selected={tab === k} onClick={() => { setTab(k); setLimit(PAGE); }} data-testid={`v5-jobs-tab-${k}`}>{label} <b>{loading ? "…" : counts[k]}</b></button>
          ))}
        </div>
        <label className="v5-jobSearch"><Search size={15} /><input value={q} onChange={(e) => { setQ(e.target.value); setLimit(PAGE); }} placeholder="Search jobs..." aria-label="Search jobs" data-testid="v5-jobs-search" /></label>
      </div>
      <div role="table" aria-label="Jobs">
        <div className="v5-tablehead" role="row"><span>JOB NUMBER</span><span>JOB NAME</span><span>LOCATION</span><span>CREW</span><span>PROGRESS</span><span>STATUS</span><span /></div>
        {loading && <div className="v5-empty"><Skel /> Loading jobs…</div>}
        {!loading && !rows.length && <div className="v5-empty" data-testid="v5-jobs-empty">{q ? "No jobs match your search." : "No jobs with this status."}</div>}
        {!loading && rows.slice(0, limit).map((j) => <JobRow key={j.id} j={j} usersById={usersById} />)}
      </div>
      {!loading && rows.length > limit && <div className="v5-more"><button className="v5-textBtn" onClick={() => setLimit(limit + PAGE)} data-testid="v5-jobs-more">Show more ({rows.length - limit} more)</button></div>}
    </section>
  );
}

export function Attention({ stats, panel, loading }) {
  const rows = [
    [ShieldAlert, "red", stats?.open_safety, "Safety items require review", "/app/safety"],
    [Clock, "amber", stats?.pending_timesheets, "Timesheets pending approval", "/app/timesheets"],
    [CalendarDays, "blue", stats?.pending_leave, "Leave requests", "/app/leave"],
    [Receipt, "pink", panel?.alerts?.unpaid_invoices, "Invoices overdue / unpaid", "/app/customer-invoices"],
    [Truck, "purple", panel?.alerts?.fleet_due, "Fleet WoF, rego or service due (30 days)", "/app/plant/fleet"],
    [Award, "grey", panel?.alerts?.certs_expiring, "Certs expiring soon", "/app/competencies"],
  ];
  return (
    <section className="v5-panel v5-attention" aria-labelledby="v5-attn-title" data-testid="v5-attention">
      <div className="v5-panelHead"><h2 id="v5-attn-title"><span className="v5-sectionIcon"><Zap /></span> Needs attention</h2></div>
      {rows.map(([Icon, tone, n, label, to]) => (
        <Link key={label} to={to} className="v5-alertrow" data-testid={`v5-alert-${to.split("/").pop()}`}>
          <Icon className={`i-${tone}`} aria-hidden="true" />
          <span className={`v5-pill p-${n ? tone : "grey"}`}>{loading ? "…" : n ?? "—"}</span>{label}<ChevronRight className="chev" aria-hidden="true" />
        </Link>
      ))}
    </section>
  );
}

const pin = (label) => L.divIcon({ className: "", html: `<span class="v5-marker"><i></i>${label}</span>`, iconSize: null });

export function MapPanel({ jobs, loading }) {
  const nav = useNavigate();
  const pts = (jobs || []).filter((j) => j.status === "in_progress" && j.geo?.lat && j.geo?.lng);
  const center = pts.length ? [pts.reduce((s, j) => s + j.geo.lat, 0) / pts.length, pts.reduce((s, j) => s + j.geo.lng, 0) / pts.length] : null;
  return (
    <section className="v5-panel v5-map" aria-labelledby="v5-map-title" data-testid="v5-map-panel">
      <div className="v5-panelHead">
        <h2 id="v5-map-title"><span className="v5-sectionIcon"><MapIcon /></span> Live job locations</h2>
        <Link className="v5-textBtn" to="/app/operations-map" data-testid="v5-map-view">View map <ArrowRight size={14} /></Link>
      </div>
      <div className="v5-mapbox">
        {!loading && center && (
          <MapContainer center={center} zoom={10} scrollWheelZoom={false} zoomControl={false} attributionControl style={{ height: "100%" }}
            whenReady={(m) => { if (pts.length > 1) m.target.fitBounds(pts.map((j) => [j.geo.lat, j.geo.lng]), { padding: [30, 30] }); }}>
            <TileLayer attribution="Tiles &copy; Esri" url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}" maxZoom={16} />
            {pts.map((j) => <Marker key={j.id} position={[j.geo.lat, j.geo.lng]} icon={pin(j.job_number || j.title)} eventHandlers={{ click: () => nav(`/app/jobs/${j.id}`) }} title={j.title} />)}
          </MapContainer>
        )}
        {!loading && !center && <div className="v5-mapEmpty" data-testid="v5-map-empty">No in-progress jobs have a mapped site address yet.<br />Add a site address to a job to see it here.</div>}
      </div>
    </section>
  );
}

const SCHED = { job: [HardHat, "#fff0e9", "#ff5f15"], fleet: [Truck, "#f2eaff", "#9252ff"], cert: [Award, "#eaf2ff", "#2475ef"], crew: [Users, "#e5f9ef", "#10a46c"] };

export function Upcoming({ panel, assignments, loading }) {
  const today = nzToday();
  const byJob = {};
  (assignments || []).filter((a) => a.job_id).forEach((a) => { (byJob[a.job_id] ||= { title: a.job_title, n: 0 }).n += 1; });
  const items = [
    ...Object.entries(byJob).map(([id, v]) => ({ kind: "crew", label: `Crew dispatched — ${v.title || "Job"}`, sub: `${v.n} crew on the day board`, link: `/app/jobs/${id}` })),
    ...((panel?.reminders || []).filter((r) => r.date === today).map((r) => ({ kind: r.kind, label: r.label, sub: "Due today", link: r.link }))),
  ];
  return (
    <section className="v5-panel v5-schedule" aria-labelledby="v5-up-title" data-testid="v5-upcoming">
      <div className="v5-panelHead">
        <h2 id="v5-up-title"><span className="v5-sectionIcon"><CalendarClock /></span> Upcoming today</h2>
        <Link className="v5-textBtn" to="/app/crew-planner?view=day" data-testid="v5-upcoming-view-all">View all <ArrowRight size={14} /></Link>
      </div>
      {loading ? <div className="v5-empty"><Skel /></div> : !items.length ? <div className="v5-empty" data-testid="v5-upcoming-empty">Nothing scheduled for today.</div> : (
        <div className="v5-scheduleGrid">
          {items.slice(0, 6).map((it, i) => {
            const [Icon, bg, fg] = SCHED[it.kind] || SCHED.job;
            return (
              <Link key={i} to={it.link}>
                <b>Today</b><span className="sicon" style={{ background: bg, color: fg }}><Icon /></span>
                <span>{it.label}<small>{it.sub}</small></span>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}
